import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import { format } from "date-fns"
import { Location } from "@/reducers/locationsSlice"
import { extractVhostFromJid } from "@/jidUtils"
import { parseHybridJsonResponse } from "./parseHybridJsonResponse"
import i18n from "@/lib/i18n/i18n"

export type CmrDocumentMetadata = {
  extractedMetadata?: {
    messageId: string
    documentType: string
    data?: Record<string, string>
    blurry?: boolean
    remarks?: string
  }
  driverContext?: {
    messageId: string
    plates: string
    location?: Location
  }
}

export type CmrOrderAnnotation = {
  processedBy: string
  processedAt: string
  chatJid: string
  note: string
  reportDate?: string
}

export type CmrReportRow = {
  deliveryOrderMessageId: string
  deliveryOrderFileUrl: string
  deliveryOrderFileName: string
  driverJid: string
  chatJid: string
  linkedDocuments: Array<{
    cmrMessageId: string
    cmrFileUrl: string
    cmrFileName: string
    timestamp: string
  }>
  metadataByUrl: Record<string, CmrDocumentMetadata>
  annotation?: CmrOrderAnnotation
}

export type UnlinkedDocument = {
  messageId: string
  timestamp: string
  fileUrl?: string
  fileName?: string
  chatJid?: string
  driverJid?: string
  /**
   * Inner author tag from the forwarded archive row — typically the driver's
   * user JID. Populated by the search ad-hoc commands but not by get_cmr_report.
   */
  author?: string
  cmrStanzaIds?: string[]
  extractedMetadata?: {
    messageId: string
    documentType: string
    data?: Record<string, string>
    blurry?: boolean
    remarks?: string
  }
  driverContext?: {
    messageId: string
    plates: string
    location?: Location
  }
  documentLink?: {
    cmrMessageId: string
    deliveryOrderMessageId: string
    timestamp?: string
  }
}

/**
 * Processed annotations for unlinked-document driver groups, keyed by the
 * group's identity (driver JID, or document messageId for driverless groups —
 * matching {@link groupUnlinkedByDriver} in CmrReport).
 */
export type UnlinkedAnnotationMap = Record<string, CmrOrderAnnotation>

type CacheEntry = {
  rows: CmrReportRow[]
  unlinked: UnlinkedDocument[]
  unlinkedAnnotations: UnlinkedAnnotationMap
}

function buildDateRange(date: Date): { start: string; end: string } {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  const end = new Date(date)
  end.setHours(23, 59, 59, 999)
  return {
    start: start.toISOString(),
    end: end.toISOString(),
  }
}

function dateKey(date: Date): string {
  return format(date, "yyyy-MM-dd")
}

export function useCmrReportData(selectedDate: Date, chatJid?: string) {
  const { client } = useContext(XmppContext)
  const [data, setData] = useState<CmrReportRow[]>([])
  const [unlinkedDocuments, setUnlinkedDocuments] = useState<
    UnlinkedDocument[]
  >([])
  const [unlinkedAnnotations, setUnlinkedAnnotations] =
    useState<UnlinkedAnnotationMap>({})
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cacheRef = useRef<Map<string, CacheEntry>>(new Map())

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      if (!client) return

      const key = dateKey(selectedDate)

      if (!forceRefresh && cacheRef.current.has(key)) {
        const cached = cacheRef.current.get(key)!
        setData(cached.rows)
        setUnlinkedDocuments(cached.unlinked)
        setUnlinkedAnnotations(cached.unlinkedAnnotations)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const vhost = extractVhostFromJid(client.jid!.toString())
        const isDemo = vhost === "demo"
        const effectiveDate = isDemo ? new Date("2026-03-27") : selectedDate
        const { start, end } = buildDateRange(effectiveDate)
        const reportDate = dateKey(effectiveDate)

        const result = await AdHocCommands.getCmrReport(
          client,
          start,
          end,
          reportDate,
          chatJid,
        )

        const parsed = await parseHybridJsonResponse<{
          rows?: CmrReportRow[]
          unlinked?: UnlinkedDocument[]
          unlinkedAnnotations?: UnlinkedAnnotationMap
        }>(result)
        let rows: CmrReportRow[] = isDemo
          ? (parsed.rows || []).map((row: CmrReportRow) => ({
              ...row,
              chatJid: row.chatJid?.replace("@drivers.", "@conference."),
            }))
          : parsed.rows || []
        if (isDemo) {
          const byKey = new Map<string, CmrReportRow>()
          for (const row of rows) {
            const localPart = (row.driverJid || "").split("@")[0]
            const key = `${localPart}|${row.deliveryOrderFileUrl}`
            const existing = byKey.get(key)
            // Prefer real user JIDs over MUC occupant-style JIDs
            const isMucJid = row.driverJid?.includes("@conference.")
            if (
              !existing ||
              (!isMucJid && existing.driverJid?.includes("@conference."))
            ) {
              byKey.set(key, row)
            }
          }
          rows = Array.from(byKey.values())
        }
        let unlinked: UnlinkedDocument[] = isDemo
          ? (parsed.unlinked || []).map((doc: UnlinkedDocument) => ({
              ...doc,
              chatJid: doc.chatJid?.replace("@drivers.", "@conference."),
            }))
          : parsed.unlinked || []
        if (isDemo) {
          const byKey = new Map<string, UnlinkedDocument>()
          for (const doc of unlinked) {
            const localPart = (doc.driverJid || "").split("@")[0]
            const key = `${localPart}|${doc.fileUrl || doc.messageId}`
            const existing = byKey.get(key)
            const isMucJid = doc.driverJid?.includes("@conference.")
            if (
              !existing ||
              (!isMucJid && existing.driverJid?.includes("@conference."))
            ) {
              byKey.set(key, doc)
            }
          }
          unlinked = Array.from(byKey.values())
        }

        const annotations: UnlinkedAnnotationMap =
          parsed.unlinkedAnnotations || {}

        cacheRef.current.set(key, {
          rows,
          unlinked,
          unlinkedAnnotations: annotations,
        })
        setData(rows)
        setUnlinkedDocuments(unlinked)
        setUnlinkedAnnotations(annotations)
      } catch (err) {
        console.error("Error fetching CMR report data:", err)
        setError(
          err instanceof Error
            ? err.message
            : i18n.t("reports.cmrReport.fetchFailed"),
        )
      } finally {
        setIsLoading(false)
      }
    },
    [client, selectedDate, chatJid],
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refetch = useCallback(() => fetchData(true), [fetchData])

  const updateAnnotation = useCallback(
    (
      deliveryOrderMessageId: string,
      annotation: CmrOrderAnnotation | undefined,
    ) => {
      const key = dateKey(selectedDate)
      const updater = (rows: CmrReportRow[]) =>
        rows.map((r) =>
          r.deliveryOrderMessageId === deliveryOrderMessageId
            ? { ...r, annotation }
            : r,
        )
      setData(updater)
      const cached = cacheRef.current.get(key)
      if (cached) {
        cacheRef.current.set(key, { ...cached, rows: updater(cached.rows) })
      }
    },
    [selectedDate],
  )

  const updateUnlinkedAnnotation = useCallback(
    (groupKey: string, annotation: CmrOrderAnnotation | undefined) => {
      const key = dateKey(selectedDate)
      const updater = (prev: UnlinkedAnnotationMap): UnlinkedAnnotationMap => {
        const next = { ...prev }
        if (annotation) {
          next[groupKey] = annotation
        } else {
          delete next[groupKey]
        }
        return next
      }
      setUnlinkedAnnotations(updater)
      const cached = cacheRef.current.get(key)
      if (cached) {
        cacheRef.current.set(key, {
          ...cached,
          unlinkedAnnotations: updater(cached.unlinkedAnnotations),
        })
      }
    },
    [selectedDate],
  )

  return {
    data,
    unlinkedDocuments,
    unlinkedAnnotations,
    isLoading,
    error,
    refetch,
    updateAnnotation,
    updateUnlinkedAnnotation,
  }
}
